
import { useShopIndex } from "queries/shops";
import { useTranslation } from "react-i18next";
import { DataGrid, GridColDef, GridValueGetterParams, frFR, GridComparatorFn, itIT, deDE, GridLocaleText } from "@mui/x-data-grid";
import { humanDate, humanDateToTime } from "utils/date";
import { getLanguageCode } from "components/LanguageSelector";
import useFilters, { filteredShop } from "pages/shops/index/useFilters";
import Filters from "pages/shops/index/Filters";
import { Feature, useAuthorization } from "user";
import { Box } from "@mui/material";

interface recordedCellsValue {
    recordedCellsCount: number;
    recordableCellsCount: number;
    completionRate: number;
    erpId: string;
}

const dayInMonthComparator: GridComparatorFn<string> = (v1, v2) => humanDateToTime(v1) - humanDateToTime(v2);
const localeCompareComparator: GridComparatorFn<string> = (v1, v2) => v1.localeCompare(v2, undefined, { numeric: true });
const recordedCellsComparator: GridComparatorFn<recordedCellsValue> = (v1, v2) => {
    let result = v2.completionRate - v1.completionRate;
    if (result === 0) {
        result = v2.erpId.localeCompare(v1.erpId, undefined, { numeric: true });
    }
    return result;
};

const buildColumns = (t: any): GridColDef[] => [
    {
        headerName: t("stats.index.lastUpdateTable.erpId"),
        field: "erpId",
        flex: 1,
        valueGetter: (params: GridValueGetterParams) => params.row.erpId,
        sortComparator: localeCompareComparator
    },
    {
        headerName: t("stats.index.lastUpdateTable.name"),
        field: "name",
        flex: 1,
        valueGetter: (params: GridValueGetterParams) => params.row.name,
    },
    {
        headerName: t("stats.index.lastUpdateTable.recordedCells"),
        field: "recordedCells",
        flex: 1,
        valueGetter: (params: GridValueGetterParams) => {
            return {
                recordedCellsCount: params.row.recordedCellsCount,
                recordableCellsCount: params.row.recordableCellsCount,
                completionRate: params.row.completionRate,
                erpId: params.row.erpId,
            };
        },
        valueFormatter: (params) => {
            return params.value.recordedCellsCount !== undefined
            ? `${params.value.recordedCellsCount} / ${params.value.recordableCellsCount}`
            : "";
        },
        sortComparator: recordedCellsComparator
    },
    {
        headerName: t("stats.index.lastUpdateTable.date"),
        field: "date",
        flex: 1,
        valueGetter: (params: GridValueGetterParams) => humanDate(params.row.activeGridUpdatedAt),
        sortComparator: dayInMonthComparator
    },
];

const LastUpdateTable = () => {
    const { t } = useTranslation();
    const hasAccess = useAuthorization();
    const { filters, setFilters, params: filtersParams, searchTerm, completionRate } = useFilters();
    const { data: shops = [] } = useShopIndex();

    const shopsFiltered = filteredShop(shops, filtersParams, searchTerm, completionRate);

    const getLocaleText = ():Partial<GridLocaleText> => {
        let baseLang;
        let lang = getLanguageCode() || 'fr';
        switch (lang) {
            case 'it':
                baseLang = itIT;
                break;
            case 'de':
                baseLang = deDE;
                break;
            default:
                baseLang = frFR;
                break;
        }
        let localeText = baseLang.components.MuiDataGrid.defaultProps.localeText;

        // Custom ask by the country
        if (lang === 'it' && localeText.MuiTablePagination) {
            localeText.MuiTablePagination.labelDisplayedRows = ({
                from,
                to,
                count
              }) => `${from}–${to} su ${count !== -1 ? count : `più su ${to}`}`
        }

        return baseLang.components.MuiDataGrid.defaultProps.localeText;
    };

    return (
        <>
            {hasAccess(Feature.ShopFilters) && <Filters filters={filters} setFilters={setFilters} />}
            <Box sx={{ height: '80vh', width: '100%', marginTop: 4 }}>
                <DataGrid
                    localeText={getLocaleText()}
                    getRowId={(row) => row.erpId}
                    rows={shopsFiltered}
                    columns={buildColumns(t)}
                    checkboxSelection={false}
                />
            </Box>
        </>
    );
};

export default LastUpdateTable;
